import { useEffect, useRef } from 'react'
import { useLocalAudioLevel, useDmLocalAudioLevel } from './useLocalAudioLevel'
import { useToastStore } from '../stores/toastStore'

/**
 * Watch the local mic level and surface a one-shot info toast when sustained
 * loudness indicates a "hot mic" — a microphone whose hardware gain (system
 * / driver / boost slider) is too high, producing residual noise on peaks
 * even after RNNoise/sendAgc.
 *
 * The detection is intentionally simple and conservative:
 *   - sample the smoothed 0..100 `useLocalAudioLevel` every 1 s
 *   - keep a rolling window of the last 30 samples (~30 s)
 *   - if the rolling AVG is ≥ `HOT_AVG_THRESHOLD` (sustained voice well
 *     above the typical -20 dBFS target) AND we haven't already toasted
 *     this session, fire ONCE.
 *
 * Toast is INFO-ONLY — steers the user toward lowering system mic
 * sensitivity. We deliberately do NOT suggest DFN in the toast (was tried
 * 2026-05-26 with a capability-gated CTA; even on a strong machine
 * (Aleksei's M1 Max, dfn_avg_ms ~5 ms, 0 drops, cpu_pressure nominal) the
 * loudness profile shifted ~5 dB louder on p50 vs RNNoise — DFN suppresses
 * the noise floor harder so sendAgc's per-speech target lands on a
 * "fuller" signal. Subjective verdict from the team: "стало слышно хуже".
 * See `feedback_dfn_capability_gate` memory for the full story. DFN
 * remains MANUAL opt-in via `localStorage.denoiserBackend='dfn'` for power
 * users; we don't push it via UI.
 *
 * Why one-shot per session: a transient burst (clearing throat into mic)
 * shouldn't keep re-firing the toast. Cleared on full page reload via
 * `sessionStorage`.
 *
 * Mount this once in `VoiceManager` (the invisible global voice-side
 * effect owner). Reads MAX of the channel local-audio-level AND the
 * DM-call local-audio-level, so the toast fires in DM-only sessions
 * too (originally read channel-only, missing DM-only callers — #3 of
 * the 2026-05-26 5-item list). Auto no-ops when not in any call (both
 * hooks return 0 with no track) — averages stay < threshold and the
 * toast never fires.
 */

// Calibration (smoothed 0..100 from useLocalAudioLevel):
//   - Typical conversational speech sits at ~30..60 (RMS ≈ 0.06..0.12)
//   - A hot mic (Aleksei I, prod 2026-05-26: avg local_total_energy ~27
//     vs Pavel L. ~2) typically lands sustained 70..90
//   - Threshold 70 → triggers on real "hot mic" without false-positives
//     on normal loud speakers
const HOT_AVG_THRESHOLD = 70
const SAMPLE_WINDOW = 30
const SAMPLE_INTERVAL_MS = 1000
const SESSION_DEDUPE_KEY = 'hotMicToastFiredThisSession'

export function useHotMicMonitor(): void {
  // Read BOTH channel and DM-call local audio level. The user's single
  // physical mic publishes to both rooms when they're in both (per-room
  // sharedMicCapture path), so loudness is the same — but useLocalAudioLevel
  // reads from `livekitStore.localAudioTrack` (channel-only) and returns 0
  // for DM-only sessions. Take MAX so a DM-only caller (no channel) also
  // triggers the hot-mic toast on sustained level. The user's sendAgc + mic
  // are shared across rooms; the toast is a per-user UX cue, not per-room.
  const channelLevel = useLocalAudioLevel()
  const dmLevel = useDmLocalAudioLevel()
  const level = Math.max(channelLevel, dmLevel)
  // Latest level mirrored into a ref so the interval (which captures the
  // ref once at mount) always reads the current value without re-creating
  // itself on every render.
  const levelRef = useRef(level)
  useEffect(() => {
    levelRef.current = level
  }, [level])

  useEffect(() => {
    const samples: number[] = []

    const tick = () => {
      samples.push(levelRef.current)
      if (samples.length > SAMPLE_WINDOW) samples.shift()
      // Only evaluate once the window is full — avoids false fires from
      // a single loud burst in the first seconds of joining.
      if (samples.length < SAMPLE_WINDOW) return
      const sum = samples.reduce((a, b) => a + b, 0)
      const avg = sum / samples.length
      if (avg < HOT_AVG_THRESHOLD) return

      // De-dupe this session via sessionStorage (cleared on full reload).
      let alreadyFired = false
      try {
        alreadyFired = sessionStorage.getItem(SESSION_DEDUPE_KEY) === '1'
      } catch { /* noop */ }
      if (alreadyFired) return
      try { sessionStorage.setItem(SESSION_DEDUPE_KEY, '1') } catch { /* noop */ }

      console.info(`[useHotMicMonitor] Hot mic detected (30s avg level=${avg.toFixed(1)}/100). Suggesting system-gain reduction.`)

      // Info-only toast (no DFN action — see header comment). Steers the
      // user toward the universally-safe fix: lower the OS-level mic
      // sensitivity. Works on any CPU, any denoiser, no surprises.
      useToastStore.getState().showToast({
        id: 'hot-mic-suggest-system-gain',
        type: 'message',
        title: 'Microphone too loud',
        body: 'Your mic level is high — noise can leak through on peaks. Lower mic sensitivity in your system settings (Windows: Sound → Device properties, macOS: System Settings → Sound → Input).',
      })
    }

    const timer = setInterval(tick, SAMPLE_INTERVAL_MS)
    return () => { clearInterval(timer) }
  }, [])
}
